import React from 'react';
import { isMobile } from 'react-device-detect';
import { matchPath, useLocation } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { Box, Stack } from '@mui/material';
import * as Sentry from '@sentry/react';

import DisabledFrontendDialog from '@/components/DisabledFrontendDialog';
import { ErrorBoundaryFallback } from '@/components/ErrorBoundaryFallback';
import { SplashScreen } from '@/components/SplashScreen';
import UserStatusBlockingScreen from '@/components/UserStatusBlockingScreen';
import { useFacilitySelectorEffects } from '@/hooks/facility-selection/useFacilitySelectorEffects';
import {
  ScreenerSettingName,
  ScreenerSettingsGuard
} from '@/hooks/screener-settings';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import InboxModal from '@/pages/AdminHub/Staff/Notifications/components/InboxModal';
import { useScreenerSettingFindOne } from '@/pages/CRM/Screener/hooks/useScreenerSettingsQuery';
import { CRMScreenerRoutes } from '@/routes/getCrmRoutes/getCrmScreenerRoutes';

import { useMobileResizeHeight } from './mobile/useMobileResizeHeight';

import { CrmRequireSelectedFacility } from './CrmRequireSelectedFacility';
import Sidebar from './Sidebar';

const LayoutContent: React.FC<
  React.PropsWithChildren<{
    currentUser: NonNullable<ReturnType<typeof useCurrentUser>['data']>;
  }>
> = ({ children, currentUser }) => {
  const { pathname } = useLocation();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isMobileViewport = useIsMobileViewport();
  useMobileResizeHeight(containerRef);
  useFacilitySelectorEffects();

  const disableFrontendSettingQuery = useScreenerSettingFindOne({
    settingName: ScreenerSettingName.DisableExacareFrontend
  });
  const { data: disableFrontendSetting, isLoading: isLoadingSetting } =
    disableFrontendSettingQuery;

  const isFrontendDisabled =
    !isLoadingSetting && disableFrontendSetting?.data?.value === 'enabled';
  const isCrmLeadESignRoute = Boolean(
    matchPath(CRMScreenerRoutes.CRMScreenerSingleLeadESignNew, pathname) ||
      matchPath(CRMScreenerRoutes.CRMScreenerSingleLeadESignRequest, pathname)
  );

  return (
    <>
      <DisabledFrontendDialog open={isFrontendDisabled} />
      {isMobileViewport && currentUser.id && (
        <InboxModal userId={currentUser.id} />
      )}
      {isMobileViewport ? (
        <>{children}</>
      ) : (
        <Stack
          ref={containerRef}
          sx={{
            height: isMobile ? window.innerHeight : '100vh'
          }}
          direction="row"
        >
          <Sidebar />
          <Stack
            component="main"
            className="main-content"
            direction="column"
            sx={{
              flex: 1,
              overflowY: isCrmLeadESignRoute ? 'hidden' : 'auto',
              overflowX: 'hidden'
            }}
          >
            <Box
              id="scrollable-content"
              sx={{
                margin: 2,
                marginLeft: 0,
                minHeight: 0,
                overflow: isCrmLeadESignRoute ? 'hidden' : 'visible',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1
              }}
            >
              <CrmRequireSelectedFacility>
                {children}
              </CrmRequireSelectedFacility>
            </Box>
          </Stack>
        </Stack>
      )}
    </>
  );
};

const Layout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();
  window.getAccessTokenSilently = getAccessTokenSilently;
  const currentUserQuery = useCurrentUser();

  React.useEffect(() => {
    if (currentUserQuery.isError) {
      Sentry.captureException(currentUserQuery.error);
    }
  }, [currentUserQuery.error, currentUserQuery.isError]);

  if (currentUserQuery.isLoading) {
    return <SplashScreen />;
  }

  if (currentUserQuery.isError) {
    return (
      <ErrorBoundaryFallback
        error={
          currentUserQuery.error instanceof Error
            ? currentUserQuery.error
            : new Error('Failed to load current user')
        }
        resetError={() => {
          currentUserQuery.refetch();
        }}
      />
    );
  }

  if (!currentUserQuery.data) {
    return <SplashScreen />;
  }

  if (
    currentUserQuery.data.status === 'pending' ||
    currentUserQuery.data.status === 'deactivated'
  ) {
    return <UserStatusBlockingScreen status={currentUserQuery.data.status} />;
  }

  const facilityIds = (currentUserQuery.data.user_facility_accesses ?? [])
    .map((access) => access.facility_id)
    .filter((id): id is string => Boolean(id));

  return (
    <ScreenerSettingsGuard facilityIds={facilityIds}>
      <LayoutContent currentUser={currentUserQuery.data}>
        {children}
      </LayoutContent>
    </ScreenerSettingsGuard>
  );
};

const LayoutWithAuthenticationRequired = (props: React.PropsWithChildren) => {
  // Set in CurrentUserPopover component before logging out
  const login_hint = localStorage.getItem('login_hint') ?? undefined;

  React.useEffect(() => {
    return () => {
      localStorage.removeItem('login_hint');
    };
  }, []);

  const Rendered = withAuthenticationRequired(Layout, {
    loginOptions: {
      login_hint
    },
    onRedirecting: () => <SplashScreen />
  });
  return <Rendered {...props} />;
};

export default LayoutWithAuthenticationRequired;
